import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useLang } from "@/contexts/LangContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import ProductImage from "@/components/ProductImage";

export default function Cart() {
    const { cart, update, remove } = useCart();
    const { t } = useLang();
    const navigate = useNavigate();

    return (
        <Layout>
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
                <h1 className="text-3xl md:text-4xl font-black text-espresso">{t("cart")}</h1>

                {cart.items.length === 0 ? (
                    <div className="mt-10 text-center py-20 bg-surface rounded-3xl border border-border" data-testid="empty-cart">
                        <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground" />
                        <p className="mt-4 text-muted-foreground">{t("emptyCart")}</p>
                        <Link to="/products">
                            <Button className="mt-6 rounded-full bg-terracotta text-white">{t("exploreProducts")}</Button>
                        </Link>
                    </div>
                ) : (
                    <div className="mt-8 grid md:grid-cols-3 gap-8">
                        <div className="md:col-span-2 space-y-3" data-testid="cart-items">
                            {cart.items.map((it) => (
                                <div key={it.product_id} className="bg-white border border-border rounded-2xl p-4 flex gap-4 items-center" data-testid={`cart-item-${it.product_id}`}>
                                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-surface shrink-0">
                                        <ProductImage
                                            src={it.image}
                                            alt={it.title}
                                            imageClassName="w-full h-full object-cover"
                                            showLabel={false}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <Link to={`/products/${it.product_id}`} className="font-bold text-espresso hover:text-terracotta transition-colors line-clamp-2">
                                            {it.title}
                                        </Link>
                                        <div className="mt-1 text-terracotta font-black">{it.price.toFixed(2)} {t("SAR")}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center border border-border rounded-full">
                                            <button onClick={() => update(it.product_id, it.quantity - 1)} className="px-3 py-1.5">−</button>
                                            <span className="px-3 font-bold">{it.quantity}</span>
                                            <button onClick={() => update(it.product_id, it.quantity + 1)} className="px-3 py-1.5">+</button>
                                        </div>
                                        <button onClick={() => remove(it.product_id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-full" data-testid={`remove-${it.product_id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div>
                            <div className="bg-white border border-border rounded-2xl p-6 sticky top-24" data-testid="cart-summary">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{t("subtotal")}</span>
                                    <span className="font-bold text-espresso">{cart.subtotal.toFixed(2)} {t("SAR")}</span>
                                </div>
                                <div className="mt-3 border-t border-border pt-3 flex justify-between text-lg font-black text-espresso">
                                    <span>{t("total")}</span>
                                    <span data-testid="cart-total">{cart.subtotal.toFixed(2)} {t("SAR")}</span>
                                </div>
                                <Button
                                    onClick={() => navigate("/checkout")}
                                    className="w-full mt-5 rounded-full h-12 bg-terracotta hover:bg-terracotta/90 text-white font-bold"
                                    data-testid="checkout-btn"
                                >
                                    {t("checkout")}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
